import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { coinArt, type CaseCoin } from '@/hooks/useAchievements'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { coinWords, TIER } from './brianCoins'
import '@/styles/coins.css'

/**
 * The unlock moment (badge unlock dev spec §3 and §4; Brian's coin-unlock.js
 * for the frosted overlay, "1 of 2" and the seeds line).
 *
 *  1. The screen dims behind a frosted overlay.
 *  2. coinShimmer(coin, { flip: true }): the shimmer runs ahead and the coin
 *     holds edge on, then turns in and settles with a slight overshoot.
 *  3. The sound, at 0.80, starts 1,110 ms after the call (390 ms after the
 *     turn begins), so its impact lands as the face snaps flat.
 *  4. ACHIEVEMENT UNLOCKED above; the name, then the words, below.
 *  5. It holds: no auto-dismiss. Continue (amber glass, Brian Oct 21) shows after about 1 s.
 *  6. On dismiss the coin flies to Achievements, and the unviewed count goes
 *     up (UnlockWatcher marks it celebrated).
 * The shimmer never plays on a locked coin: this only ever shows an earned one.
 */

type ShimmerWindow = { coinShimmer?: (el: Element, o?: object) => Promise<void> }

let shimmerLoad: Promise<void> | null = null
/** Injects the shimmer script once per page. */
function loadShimmer(): Promise<void> {
  if (shimmerLoad) return shimmerLoad
  shimmerLoad = new Promise<void>((resolve, reject) => {
    if ((window as unknown as ShimmerWindow).coinShimmer) { resolve(); return }
    const script = document.createElement('script')
    script.src = '/coins/coinshimmer.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => { shimmerLoad = null; reject(new Error('coin shimmer did not load')) }
    document.head.appendChild(script)
  })
  return shimmerLoad
}

/** Spec §4: the turn starts 0.80 × 900 = 720 ms after the call; the sound
 *  starts 390 ms after the turn. */
const SOUND_AT_MS = 1110
const SOUND_VOLUME = 0.8
/** Spec §3.6: the prompt appears once there has been time to read. */
const PROMPT_AT_MS = 1000

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Where the coin flies on dismiss: Achievements in the sidebar, or the
 *  initials (Settings, where Achievements lives) on the phone. */
function flyTarget(): DOMRect | null {
  const el = document.querySelector('.gg-side a[href="/app/achievements"]')
    ?? document.querySelector('.gg-initials')
    ?? document.querySelector('a[href="/app/achievements"]')
  return el ? el.getBoundingClientRect() : null
}

function flyCoin(from: HTMLElement, src: string) {
  if (reducedMotion()) return
  const a = from.getBoundingClientRect()
  const b = flyTarget()
  if (!b || a.width === 0) return
  const img = document.createElement('img')
  img.src = src
  img.alt = ''
  img.className = 'unlock-fly'
  Object.assign(img.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px` })
  document.body.appendChild(img)
  const dx = b.left + b.width / 2 - (a.left + a.width / 2)
  const dy = b.top + b.height / 2 - (a.top + a.height / 2)
  const s = Math.max(0.08, Math.min(b.width, b.height) / a.width)
  const anim = img.animate([
    { transform: 'translate(0, 0) scale(1)', opacity: 1 },
    { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 40}px) scale(${0.55})`, opacity: 1, offset: 0.45 },
    { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0.2 },
  ], { duration: 720, easing: 'cubic-bezier(.45,.05,.55,.95)', fill: 'forwards' })
  anim.onfinish = () => img.remove()
  window.setTimeout(() => img.remove(), 1200)
}

export interface UnlockModalProps {
  coin: CaseCoin
  /** "1 of 2" when coins queue. */
  index?: number
  total?: number
  onClose: () => void
  /** "See your coins": leave for Achievements. */
  onSee?: () => void
}

export function UnlockModal({ coin, index = 1, total = 1, onClose, onSee }: UnlockModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const coinRef = useRef<HTMLImageElement>(null)
  const [prompt, setPrompt] = useState(false)
  const [shown, setShown] = useState(false)
  const done = useRef(false)

  useFocusTrap(panelRef, true)

  useEffect(() => {
    done.current = false
    setPrompt(false)
    setShown(false)
    const timers: number[] = []
    let audio: HTMLAudioElement | null = null
    const shown0 = window.setTimeout(() => setShown(true), 20)
    timers.push(shown0)
    timers.push(window.setTimeout(() => setPrompt(true), PROMPT_AT_MS))

    if (reducedMotion()) {
      // The shimmer resolves at once under reduced motion; the sound stays.
      coinRef.current?.classList.remove('is-edge')
      timers.push(window.setTimeout(() => play(), 0))
    } else {
      loadShimmer()
        .then(() => {
          const el = coinRef.current
          const shimmer = (window as unknown as ShimmerWindow).coinShimmer
          if (!el || !shimmer) { el?.classList.remove('is-edge'); return }
          // Measured from the call: the shimmer and the sound share one clock.
          void shimmer(el, { flip: true })
          el.classList.remove('is-edge')
          timers.push(window.setTimeout(play, SOUND_AT_MS))
        })
        .catch(() => { coinRef.current?.classList.remove('is-edge'); play() })
    }

    function play() {
      // Browsers may refuse audio; a silent unlock is still an unlock.
      try {
        audio = new Audio('/coins/unlock.wav')
        audio.volume = SOUND_VOLUME
        const p = audio.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
      } catch { /* no sound, no problem */ }
    }

    return () => { timers.forEach((t) => window.clearTimeout(t)); audio?.pause() }
  }, [coin.id])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  function dismiss() {
    if (done.current || !prompt) return
    done.current = true
    if (coinRef.current) flyCoin(coinRef.current, coinArt(coin.id, true, 512))
    onClose()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); dismiss() }
  }

  return (
    <div className={`unlock2${shown ? ' is-on' : ''}`} role="presentation" onKeyDown={onKeyDown}>
      <div className="unlock2__panel" role="dialog" aria-modal="true" aria-labelledby="unlock-name" aria-describedby="unlock-desc" tabIndex={-1} ref={panelRef}>
        {total > 1 && <span className="unlock2__of">{index} of {total}</span>}
        <p className="unlock2__eb">Achievement unlocked</p>
        {coin.is_secret && <p className="unlock2__hid">A hidden coin</p>}
        <div className="unlock2__stage">
          <div className="unlock2__glow" aria-hidden="true" />
          <img ref={coinRef} className="unlock2__coin is-edge" src={coinArt(coin.id, true, 512)} alt={coin.name} width={512} height={512} decoding="async" />
        </div>
        <h2 className="unlock2__name" id="unlock-name">{coin.name}</h2>
        {coin.tier && <div className="unlock2__tier">{TIER[coin.tier]}</div>}
        <p className="unlock2__desc" id="unlock-desc">{coinWords(coin)}</p>
        {coin.reward_seeds > 0 && <div className="unlock2__seeds">+{coin.reward_seeds} seeds</div>}
        <div className={`unlock2__acts${prompt ? ' is-on' : ''}`} aria-hidden={!prompt}>
          <button type="button" className="gg-btn gg-btn--amber unlock2__go" tabIndex={prompt ? 0 : -1}
            onClick={(e) => { e.stopPropagation(); dismiss() }}>Continue</button>
          {onSee && (
            <button type="button" className="unlock2__see" tabIndex={prompt ? 0 : -1}
              onClick={(e) => { e.stopPropagation(); if (!prompt) return; done.current = true; onSee() }}>
              See your coins
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
